import type {
  ManagerialDashboard,
  ManagerialDashboardSection,
  ManagerialDashboardSectionResponse,
} from '@/types/api'
import type { FetchManagerialDashboardParams } from '@/lib/api/managerial-dashboard-admin.service'
import {
  DASHBOARD_SECTIONS,
  sectionQuery,
  type DashboardQuery,
} from './dashboard-sections'
import {
  resolveManagerialDashboardError,
  resolveManagerialDashboardSection,
} from './managerial-dashboard'

export type DashboardBlockState = {
  data: ManagerialDashboardSectionResponse | null
  isLoading: boolean
  error: string | null
}
export type DashboardBlocks = Record<ManagerialDashboardSection, DashboardBlockState>
type Job = {
  section: ManagerialDashboardSection
  key: string
  params: DashboardQuery
}
type Fetcher = (params: FetchManagerialDashboardParams) => Promise<unknown>

function emptyBlocks(): DashboardBlocks {
  return Object.fromEntries(
    DASHBOARD_SECTIONS.map((section) => [
      section,
      { data: null, isLoading: true, error: null },
    ])
  ) as DashboardBlocks
}

/** One queue owns initial load, filter changes, refresh and retries. */
export class DashboardLoader {
  private blocks = emptyBlocks()
  private readonly empty = emptyBlocks()
  private listeners = new Set<() => void>()
  private jobs = new Map<ManagerialDashboardSection, Job>()
  private controllers = new Map<ManagerialDashboardSection, AbortController>()
  private queue: Job[] = []
  private running = 0
  private active = false
  private denied = false
  private params: DashboardQuery | null = null
  private audience: DashboardQuery['audience']
  private timer: ReturnType<typeof setTimeout> | undefined
  private filterOptions: ManagerialDashboard['filter_options'] | undefined
  private lastCompleted: number | null = null
  private completedAt = new Map<ManagerialDashboardSection, number>()
  private readonly fetcher: Fetcher

  constructor(fetcher: Fetcher) {
    this.fetcher = fetcher
  }
  getSnapshot = () => this.blocks
  getEmptySnapshot = () => this.empty
  getAudience = () => this.audience
  getFilterOptions = () => this.filterOptions
  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }
  private publish(section?: ManagerialDashboardSection, state?: DashboardBlockState) {
    this.blocks =
      section && state ? { ...this.blocks, [section]: state } : { ...this.blocks }
    this.listeners.forEach((listener) => listener())
  }

  activate() {
    this.active = true
  }
  dispose() {
    this.active = false
    clearTimeout(this.timer)
    this.queue = []
    this.jobs.clear()
    this.completedAt.clear()
    this.controllers.forEach((controller) => controller.abort())
    this.controllers.clear()
  }

  configure(params: DashboardQuery) {
    const changedAudience = this.params !== null && this.audience !== params.audience
    if (changedAudience) {
      this.queue = []
      this.jobs.clear()
      this.controllers.forEach((controller) => controller.abort())
      this.controllers.clear()
      this.blocks = emptyBlocks()
      this.filterOptions = undefined
      this.lastCompleted = null
      this.completedAt.clear()
      this.denied = false
      this.publish()
    }
    if (this.filterOptions && this.params?.institutionId !== params.institutionId) {
      this.filterOptions = {
        ...this.filterOptions,
        campaigns: this.filterOptions.campaigns.filter(
          (campaign) =>
            params.institutionId === null ||
            campaign.institution_id === params.institutionId
        ),
      }
    }
    this.params = params
    this.audience = params.audience
    if (this.denied) return
    for (const section of DASHBOARD_SECTIONS) {
      const normalized = sectionQuery(section, params)
      const key = JSON.stringify(normalized)
      if (this.jobs.get(section)?.key !== key)
        this.enqueue(section, normalized, key, true)
    }
    this.pump()
  }

  refresh = (section?: ManagerialDashboardSection, manual = true) => {
    if (!this.active || !this.params) return
    if (this.denied && !manual) return
    if (!manual && this.queue.length > 0) {
      // A paused initial load may resume much later; refresh only finished, stale blocks.
      for (const target of DASHBOARD_SECTIONS) {
        const completed = this.completedAt.get(target)
        if (
          completed !== undefined &&
          !this.blocks[target].isLoading &&
          Date.now() - completed >= 60_000
        ) {
          const normalized = sectionQuery(target, this.params)
          this.enqueue(target, normalized, JSON.stringify(normalized), false)
        }
      }
      this.pump()
      return
    }
    if (
      !manual &&
      this.lastCompleted !== null &&
      Date.now() - this.lastCompleted < 60_000
    )
      return
    if (!section && this.isBusy()) return
    this.denied = false
    for (const target of section ? [section] : DASHBOARD_SECTIONS) {
      if (this.blocks[target].isLoading && this.jobs.has(target)) continue
      const normalized = sectionQuery(target, this.params)
      this.enqueue(target, normalized, JSON.stringify(normalized), false)
    }
    this.pump()
  }

  private isBusy() {
    return this.running > 0 || this.queue.length > 0
  }
  private enqueue(
    section: ManagerialDashboardSection,
    params: DashboardQuery,
    key: string,
    clear: boolean
  ) {
    clearTimeout(this.timer)
    this.controllers.get(section)?.abort()
    this.queue = this.queue.filter((job) => job.section !== section)
    const job = { section, params, key }
    if (clear) this.completedAt.delete(section)
    this.jobs.set(section, job)
    this.queue.push(job)
    this.queue.sort(
      (left, right) =>
        DASHBOARD_SECTIONS.indexOf(left.section) -
        DASHBOARD_SECTIONS.indexOf(right.section)
    )
    this.publish(section, {
      data: clear ? null : this.blocks[section].data,
      isLoading: true,
      error: null,
    })
  }

  private pump() {
    if (!this.active || this.denied) return
    while (
      document.visibilityState === 'visible' &&
      this.running < 2 &&
      this.queue.length > 0
    ) {
      const job = this.queue.shift()!
      if (this.jobs.get(job.section) !== job) continue
      this.running++
      void this.run(job)
    }
    if (!this.isBusy()) {
      this.lastCompleted = Date.now()
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        if (document.visibilityState === 'visible') this.refresh(undefined, false)
      }, 60_000)
    }
  }

  private async run(job: Job) {
    const controller = new AbortController()
    this.controllers.set(job.section, controller)
    let timeout: ReturnType<typeof setTimeout> | undefined
    const current = () =>
      this.active && !this.denied && this.jobs.get(job.section) === job
    try {
      const response = await Promise.race([
        this.fetcher({ ...job.params, section: job.section, signal: controller.signal }),
        new Promise<never>((_, reject) => {
          timeout = setTimeout(() => {
            reject(new Error('Tempo limite ao consultar este bloco. Tente novamente.'))
            controller.abort()
          }, 30_000)
          controller.signal.addEventListener(
            'abort',
            () => reject(new DOMException('Cancelado', 'AbortError')),
            { once: true }
          )
        }),
      ])
      if (!current()) return
      const data = resolveManagerialDashboardSection(response, job.section)
      if (!data) throw new Error('Não foi possível validar os dados deste bloco.')
      this.completedAt.set(job.section, Date.now())
      if (job.section === 'overview') this.filterOptions = data.filter_options
      this.publish(job.section, { data, isLoading: false, error: null })
    } catch (error) {
      if (!current()) return
      this.completedAt.set(job.section, Date.now())
      if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        (error.status === 401 || error.status === 403)
      ) {
        this.denied = true
        this.completedAt.clear()
        this.filterOptions = undefined
        this.queue = []
        this.jobs.clear()
        this.controllers.forEach((active) => active.abort())
        this.blocks = Object.fromEntries(
          DASHBOARD_SECTIONS.map((section) => [
            section,
            {
              data: null,
              isLoading: false,
              error: 'Acesso ao painel indisponível. Verifique sua sessão ou permissão.',
            },
          ])
        ) as DashboardBlocks
        clearTimeout(this.timer)
        this.publish()
      } else {
        const missingRoute =
          typeof error === 'object' &&
          error !== null &&
          'status' in error &&
          error.status === 404
        const message =
          error instanceof Error &&
          (/^Tempo limite ao consultar este bloco/.test(error.message) ||
            error.message === 'Não foi possível validar os dados deste bloco.')
            ? error.message
            : missingRoute
              ? 'Rota do bloco indisponível. Verifique a atualização do backend do painel.'
              : resolveManagerialDashboardError(error)
        this.publish(job.section, {
          data: this.blocks[job.section].data,
          isLoading: false,
          error: message,
        })
      }
    } finally {
      clearTimeout(timeout)
      if (this.controllers.get(job.section) === controller)
        this.controllers.delete(job.section)
      this.running--
      this.pump()
    }
  }
}
